import { useEffect } from 'react'

import {
  NotificationConditionTypeEnum,
  getNotificationConditionTypeString,
  notificationByIdQuery,
  notificationConditionTypeArray,
} from '@/entities/notifications'
import { type UploadFileWithId, UploadPicture } from '@/features/upload-files'
import { type NotificationConditionTypeEnumEnum } from '@/shared/api'
import {
  hasFormChanged,
  parseDateStringToDayjsDate,
  parseDateStringToDayjsTime,
} from '@/shared/lib/form-utils'
import { showNotificationWarningFx } from '@/shared/lib/providers'
import {
  HidingSpin,
  SelfValidateInput,
  SelfValidateTextArea,
} from '@/shared/ui'
import { PrimaryButton } from '@/shared/ui'
import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import { DatePicker, Flex, Form, Popover, Radio, Space, TimePicker } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { useUnit } from 'effector-react'
import { useFormik } from 'formik'
import { z } from 'zod'
import { toFormikValidationSchema } from 'zod-formik-adapter'

import { MAX_FIELD_LENGTH } from './lib/constants'
import { getNotificationSubmitRequestByFormValues } from './lib/get-notification-by-form'
import {
  $submittingNotification,
  notificationSubmitted,
} from './model/notification-form.model'

const notificationSchema = z.object({
  name: z.string().max(MAX_FIELD_LENGTH.name),
  description: z.string().max(MAX_FIELD_LENGTH.description),
  pictureFile: z.custom<UploadFileWithId>().nullable().optional(),
  conditionType: z.custom<NotificationConditionTypeEnumEnum>(),
  dates: z
    .array(
      z.object({
        date: z.custom<Dayjs>().nullable(),
        time: z.custom<Dayjs>().nullable(),
      }),
    )
    .optional(),
})

export type NotificationFormModel = z.infer<typeof notificationSchema>

export const NotificationForm = () => {
  const {
    data: notificationData,
    pending: notificationPending,
    error: notificationError,
  } = useUnit(notificationByIdQuery)
  const queryStatus = useUnit(notificationByIdQuery.$status)

  const submit = useUnit(notificationSubmitted)
  const submitting = useUnit($submittingNotification)

  const formik = useFormik<NotificationFormModel>({
    initialValues: notificationData
      ? {
          name: notificationData.name ?? '',
          description: notificationData.description ?? '',
          pictureFile: notificationData.picture
            ? ({
                id: notificationData.picture.id,
                uid: notificationData.picture.id,
                name: notificationData.picture.name,
                status: 'done',
                url: notificationData.picture.fullCdnUrl,
              } as UploadFileWithId)
            : null,
          conditionType: notificationData.conditionType,
          dates: [
            {
              date: notificationData.sendOnDate
                ? parseDateStringToDayjsDate(notificationData.sendOnDate)
                : null,
              time: notificationData.sendOnDate
                ? parseDateStringToDayjsTime(notificationData.sendOnDate)
                : null,
            },
          ],
        }
      : {
          name: '',
          description: '',
          pictureFile: null,
          conditionType: notificationConditionTypeArray[0],
          dates: [{ date: null, time: null }],
        },
    validationSchema: toFormikValidationSchema(notificationSchema),
    validateOnMount: false,
    onSubmit: (values, { setSubmitting }) => {
      if (
        values.conditionType === NotificationConditionTypeEnum.AtTime &&
        (!values.dates || values.dates.find((d) => !d.date || !d.time))
      ) {
        {
          if (values.dates) {
            const emptyDatesIndexes = values.dates.reduce<number[]>(
              (acc, d, idx) => {
                if (!d.date || !d.time) acc.push(idx)
                return acc
              },
              [],
            )
            emptyDatesIndexes.forEach((idx) => {
              formik.setFieldError(`dates.${idx}.date`, 'Обязательное поле')
            })
          }

          setSubmitting(false)
          return
        }
      }

      const submittedData = getNotificationSubmitRequestByFormValues(
        values,
        isEdit,
      )
      submit(submittedData)
      setSubmitting(false)
    },
  })

  const { isValid, isValidating, isSubmitting } = formik

  const isDirty = hasFormChanged(formik.values, formik.initialValues)

  useEffect(() => {
    if (isSubmitting && !isValid && !isValidating) {
      void showNotificationWarningFx({
        message: 'Не все поля заполнены',
      })
    }
  }, [isSubmitting, isValid, isValidating])

  useEffect(() => {
    if (isDirty) {
      const handler = (e: BeforeUnloadEvent) => {
        e.preventDefault()
      }
      window.addEventListener('beforeunload', handler)
      return () => {
        window.removeEventListener('beforeunload', handler)
      }
    }
  }, [isDirty])

  const isEdit = Boolean(notificationData)

  const alreadySent = Boolean(
    (formik.values.conditionType === NotificationConditionTypeEnum.Instant &&
      notificationData) ||
      (formik.values.conditionType === NotificationConditionTypeEnum.AtTime &&
        notificationData?.sendOnDate &&
        dayjs(notificationData.sendOnDate).isBefore(dayjs())),
  )

  const formControlButtonsDisabled =
    !hasFormChanged(formik.values, formik.initialValues) ||
    submitting ||
    alreadySent

  return (
    <HidingSpin
      spinning={
        queryStatus !== 'initial' &&
        (notificationPending || (!notificationData && !notificationError))
      }
    >
      <div style={{ maxWidth: '600px' }}>
        <Form.Item
          layout="vertical"
          label="Заголовок"
          required
          validateStatus={
            formik.errors.name && formik.touched.name ? 'error' : ''
          }
        >
          <SelfValidateInput
            name="name"
            showCount
            maxLength={MAX_FIELD_LENGTH.name}
            value={formik.values.name}
            onBlur={() => {
              void formik.setFieldTouched('name', true)
            }}
            onChange={(value) => {
              void formik.setFieldValue('name', value)
            }}
          />
        </Form.Item>

        <Form.Item
          layout="vertical"
          label="Содержание"
          required
          validateStatus={
            formik.errors.description && formik.touched.description
              ? 'error'
              : ''
          }
        >
          <SelfValidateTextArea
            showCount
            maxLength={MAX_FIELD_LENGTH.description}
            value={formik.values.description}
            onBlur={() => {
              void formik.setFieldTouched('description', true)
            }}
            onChange={(value) => {
              void formik.setFieldValue('description', value)
            }}
          />
        </Form.Item>

        <Form.Item layout="vertical" label="Обложка">
          <UploadPicture
            value={formik.values.pictureFile ?? undefined}
            onChange={(fileList) => {
              const file = fileList[0] ?? null
              void formik.setFieldValue('pictureFile', file)
            }}
            onReset={() => {
              void formik.setFieldValue('pictureFile', null)
            }}
          />
        </Form.Item>

        <Form.Item layout="vertical" label="Условия отправки" required>
          <Radio.Group
            block
            options={notificationConditionTypeArray.map(
              (notificationConditionType) => ({
                value: notificationConditionType,
                label: getNotificationConditionTypeString(
                  notificationConditionType,
                ),
              }),
            )}
            optionType="button"
            value={formik.values.conditionType}
            onChange={(value) => {
              const newConditionType: NotificationConditionTypeEnum =
                value.target.value
              void formik.setFieldValue('conditionType', newConditionType)
            }}
            buttonStyle="solid"
          />
        </Form.Item>

        {formik.values.conditionType ===
          NotificationConditionTypeEnum.AtTime && (
          <Form.Item
            layout="vertical"
            label={
              formik.values.dates && formik.values.dates.length > 1
                ? 'Даты отправки'
                : 'Дата отправки'
            }
            required
          >
            {formik.values.dates?.map((item, index) => (
              <Form.Item
                key={index}
                noStyle
                validateStatus={
                  formik.errors.dates &&
                  formik.errors.dates[index] &&
                  formik.touched.dates &&
                  Array.isArray(formik.touched.dates) &&
                  formik.touched.dates[index]
                    ? 'error'
                    : undefined
                }
              >
                <Flex
                  key={index}
                  gap={8}
                  align="center"
                  style={{ marginBottom: 8 }}
                >
                  <DatePicker
                    inputReadOnly
                    value={item.date}
                    onBlur={() => {
                      void formik.setFieldTouched(`dates.${index}.date`, true)
                    }}
                    onChange={(value) => {
                      const newDates = Array.isArray(formik.values.dates)
                        ? formik.values.dates
                        : []
                      newDates[index] = { ...newDates[index], date: value }
                      void formik.setFieldValue('dates', newDates)
                    }}
                    minDate={dayjs()}
                    style={{ width: '70%' }}
                  />
                  <TimePicker
                    inputReadOnly
                    value={item.time}
                    onBlur={() => {
                      void formik.setFieldTouched(`dates.${index}.time`, true)
                    }}
                    defaultOpenValue={dayjs('00:00:00', 'HH:mm:ss')}
                    onChange={(value) => {
                      const newDates = Array.isArray(formik.values.dates)
                        ? formik.values.dates
                        : []
                      newDates[index] = { ...newDates[index], time: value }
                      void formik.setFieldValue('dates', newDates)
                    }}
                    style={{ width: '30%' }}
                  />

                  {/* можно удалять только при создании */}
                  {!isEdit &&
                    formik.values.dates &&
                    formik.values.dates.length > 1 && (
                      <span
                        style={{ cursor: 'pointer', color: '#ff4d4f' }}
                        onClick={() => {
                          const newDates = Array.isArray(formik.values.dates)
                            ? formik.values.dates
                            : []
                          newDates.splice(index, 1)
                          void formik.setFieldValue('dates', newDates)
                        }}
                      >
                        <CloseOutlined />
                      </span>
                    )}
                </Flex>
              </Form.Item>
            ))}

            {/* кнопку "Добавить" показываем только при создании */}
            {!isEdit && (
              <PrimaryButton
                type="dashed"
                onClick={() => {
                  void formik.setFieldValue('dates', [
                    ...(Array.isArray(formik.values.dates)
                      ? formik.values.dates
                      : []),
                    { date: null, time: null },
                  ])
                }}
                icon={<PlusOutlined />}
              >
                Добавить дату
              </PrimaryButton>
            )}
          </Form.Item>
        )}

        <Space>
          <Popover
            open={alreadySent ? undefined : false}
            content={<>Уведомление уже было отправлено</>}
            placement="bottom"
          >
            <PrimaryButton
              disabled={formControlButtonsDisabled}
              loading={submitting}
              onClick={() => {
                formik.handleSubmit()
              }}
            >
              {isEdit ? 'Сохранить' : 'Создать'}
            </PrimaryButton>
          </Popover>
        </Space>
      </div>
    </HidingSpin>
  )
}
